import {useEffect} from "react";
import {create} from "zustand";
import {Col} from "@cdx/ds";
import {useModalWithData} from "../../components/Modal/Modals";
import {postError} from "../../lib/error-logger";
import useMutation from "../../lib/hooks/useMutation";
import type {SecondFactorResult} from "./SecondFactorStep";
import {SecondFactorStep} from "./SecondFactorStep";

/**
 * Every login goes through `passSecondFactor`, so no login button can forget the second factor:
 * `useOAuthFlow` calls it for each popup, the login form for `users.login`. The step itself is
 * rendered by whichever host is mounted, never by the caller.
 *
 * - `SecondFactorHost` sits once in each app root (`Root`, `SteamyApp`) and shows the step in a modal.
 * - `useInlineSecondFactor` lets a login form show it in place of itself; while one is mounted the
 *   root host stays hidden.
 */

export type SecondFactorChallenge = {
  name: string | null;
  emailHint: string;
  secondFactors: ("passkey" | "totp")[];
};

/** What `startSession` answers, from `users.login` or an OAuth popup. */
export type SessionStart =
  | {secondFactorRequired?: false}
  | ({secondFactorRequired: true} & SecondFactorChallenge);

type Pending = {
  challenge: SecondFactorChallenge;
  resolve: (result: SecondFactorResult | null) => void;
};

type GateState = {pending: Pending | null; rootHosts: number; inlineHosts: number};

const useGate = create<GateState>(() => ({pending: null, rootHosts: 0, inlineHosts: 0}));

/** Resolves once the login is complete, or with `null` when the user went back instead. */
export const passSecondFactor = (start: SessionStart): Promise<SecondFactorResult | null> => {
  if (!start.secondFactorRequired) return Promise.resolve({usedRecoveryCode: false});
  const {pending, rootHosts, inlineHosts} = useGate.getState();
  if (rootHosts + inlineHosts === 0) {
    const msg = "A second factor was requested, but no SecondFactorHost is mounted";
    console.error(msg);
    postError(new Error(msg), msg);
  }
  pending?.resolve(null);
  return new Promise((resolve) => {
    const {name, emailHint, secondFactors} = start;
    const challenge = {name, emailHint, secondFactors};
    useGate.setState({pending: {challenge, resolve}});
  });
};

const settle = (result: SecondFactorResult | null) => {
  const {pending} = useGate.getState();
  useGate.setState({pending: null});
  pending?.resolve(result);
};

const useRegisterHost = (kind: "rootHosts" | "inlineHosts") => {
  useEffect(() => {
    useGate.setState((s) => ({[kind]: s[kind] + 1}));
    return () => useGate.setState((s) => ({[kind]: s[kind] - 1}));
  }, [kind]);
};

const renderStep = (challenge: SecondFactorChallenge) => (
  <SecondFactorStep
    name={challenge.name}
    emailHint={challenge.emailHint}
    secondFactors={challenge.secondFactors}
    onDone={settle}
    onCancel={() => settle(null)}
  />
);

/** The step in place of the calling form, or `null` while no second factor is pending. */
export const useInlineSecondFactor = () => {
  useRegisterHost("inlineHosts");
  const pending = useGate((s) => s.pending);
  return pending && renderStep(pending.challenge);
};

/** Mounted once per app root, inside its `MateContextProvider`. */
export const SecondFactorHost = () => {
  useRegisterHost("rootHosts");
  const [doCancel] = useMutation("secondFactor", "cancel");
  const pending = useGate((s) => (s.inlineHosts > 0 ? null : s.pending));

  // a timed-out session rejects the cancel; closing is right either way
  const handleClose = () => {
    const close = () => settle(null);
    void doCancel({}).then(close, close);
  };

  const renderModal = useModalWithData(pending?.challenge ?? null, {
    onClose: handleClose,
    colorTheme: "white",
    width: 420,
  });
  return renderModal((challenge) => <Col pa="24px">{renderStep(challenge)}</Col>);
};
